import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { isOnline } from "@/offline/online-status";
import { isPinnedSync } from "@/offline/pins";
import { pinProject, type AuthFetch } from "@/offline/sync";

/**
 * How long the project view must stay open before we auto-cache it. A short
 * dwell means an accidental tap into a project (immediately backing out, or
 * flicking between projects) never kicks off a ~100–150 MB download. See
 * `specs/technical/auto-cache-on-open.spec.md`.
 */
export const AUTO_CACHE_DWELL_MS = 3000;

interface UseAutoCacheOnOpenOptions {
  projectId: string;
  /**
   * Whether the user can view this project's records. A project with no
   * viewable records has nothing useful to cache (and the records fetch would
   * 403), so we skip auto-cache for it.
   */
  canViewRecords: boolean;
  /**
   * Gate from the host: false while the project is still loading or errored, so
   * the hook no-ops until there's actually a project to cache. (Hooks can't be
   * conditional, so the host always calls this and toggles `enabled`.)
   */
  enabled: boolean;
}

/**
 * Auto-cache the project the user just opened for offline use, after a short
 * dwell. Reuses the manual pin engine (`pinProject`) in silent mode, tagged
 * `source: "auto"`. No-ops when disabled, when the user can't view records,
 * when offline, or when the project is already pinned.
 *
 * See `specs/technical/auto-cache-on-open.spec.md` for the full contract.
 */
export function useAutoCacheOnOpen({
  projectId,
  canViewRecords,
  enabled,
}: UseAutoCacheOnOpenOptions): void {
  const queryClient = useQueryClient();
  const fetchWithAuth = useAuthenticatedFetch();

  // Hold the latest fetch/queryClient in refs so the dwell effect depends only
  // on the meaningful inputs. Otherwise a re-render that mints a new
  // fetchWithAuth would clear and restart the timer, and a component
  // re-rendering faster than the dwell would never auto-cache. The refs are
  // synced in an effect — writing them during render violates react-hooks/refs.
  const queryClientRef = useRef(queryClient);
  const fetchWithAuthRef = useRef<AuthFetch>(fetchWithAuth);
  useEffect(() => {
    queryClientRef.current = queryClient;
    fetchWithAuthRef.current = fetchWithAuth;
  });

  useEffect(() => {
    if (!enabled || !canViewRecords) return;
    // Don't even arm the dwell while offline. Opening a project offline is a
    // no-op (we can't download), and arming here would let a reconnect within
    // the 3s start a surprise background download. (Online is re-checked
    // post-dwell too, for a drop that happens during the dwell.)
    if (!isOnline()) return;
    // Already cached (auto or manual) — nothing to arm.
    if (isPinnedSync(projectId)) return;

    const timer = setTimeout(() => {
      // Re-check after the dwell — connectivity or pin state may have changed
      // during it. We never download offline; wifi-vs-cellular is undetectable
      // on iOS, so `isOnline()` is the only network gate.
      if (!isOnline()) return;
      if (isPinnedSync(projectId)) return;
      void pinProject({
        projectId,
        queryClient: queryClientRef.current,
        fetchWithAuth: fetchWithAuthRef.current,
        silent: true,
        source: "auto",
      }).catch(() => {
        /* failures are already captured to Sentry inside pinProject */
      });
    }, AUTO_CACHE_DWELL_MS);

    return () => clearTimeout(timer);
  }, [projectId, canViewRecords, enabled]);
}
